/**
 * Creatable Multi-Select Component
 *
 * A multi-select component with the ability to create new options on the fly.
 * Uses shadcn/ui Popover + Command pattern.
 *
 * Features:
 * - Multi-select with checkboxes
 * - Create new options dynamically
 * - Search functionality
 * - Controlled component API
 * - Validation of new entries
 */

import { useMemo, useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { CheckIcon } from "@/components/ui/icons/check-icon";
import {
  dropdownChevronClassName,
  dropdownTriggerClassName,
} from "@/components/ui/dropdown-trigger-styles";

export interface CreatableOption {
  value: string;
  label: string;
  __isNew__?: boolean;
}

export interface CreatableSelectProps {
  options: CreatableOption[];
  value: CreatableOption[];
  onChange: (value: CreatableOption[]) => void;
  placeholder?: string;
  createLabel?: string; // e.g., "Create tag: {search}"
  className?: string;
  disabled?: boolean;
  closeMenuOnSelect?: boolean;
  matchTriggerWidth?: boolean;
  isValidNewOption?: (inputValue: string) => boolean;
  formatCreateLabel?: (inputValue: string) => string;
  onCreateOption?: (inputValue: string) => void;
}

export function CreatableSelect({
  options,
  value,
  onChange,
  placeholder = "Select or create...",
  createLabel = 'Create "{search}"',
  className,
  disabled = false,
  closeMenuOnSelect = false,
  matchTriggerWidth = false,
  isValidNewOption = (input) => input.trim().length > 0,
  formatCreateLabel = (input) => createLabel.replace("{search}", input),
  onCreateOption,
}: CreatableSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [triggerWidth, setTriggerWidth] = useState<number>(0);
  const [isCreating, setIsCreating] = useState(false);
  const isCreatingRef = useRef(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Measure trigger width when component mounts and when popover opens
  useEffect(() => {
    if (matchTriggerWidth && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open, matchTriggerWidth]);

  // Get selected option values
  const selectedValues = useMemo(() => value.map((v) => v.value), [value]);

  // Check if search value matches an existing option
  const isExistingOption = useMemo(() => {
    const lowerSearch = searchValue.toLowerCase();
    return options.some((opt) => opt.label.toLowerCase() === lowerSearch);
  }, [options, searchValue]);

  // Should show create option
  const showCreateOption = useMemo(() => {
    return (
      searchValue.trim().length > 0 &&
      !isExistingOption &&
      isValidNewOption(searchValue)
    );
  }, [searchValue, isExistingOption, isValidNewOption]);

  const toggleOption = (option: CreatableOption) => {
    const isSelected = selectedValues.includes(option.value);

    if (isSelected) {
      // Remove option
      const newValue = value.filter((v) => v.value !== option.value);
      onChange(newValue);
    } else {
      // Add option
      onChange([...value, option]);
    }

    // Close menu if closeMenuOnSelect is true
    if (closeMenuOnSelect) {
      setOpen(false);
    }
  };

  const handleCreateOption = () => {
    if (isCreating || isCreatingRef.current) return; // Prevent duplicate creation

    const trimmedValue = searchValue.trim();
    if (!isValidNewOption(trimmedValue)) return;

    isCreatingRef.current = true;
    setIsCreating(true);
    try {
      const newOption: CreatableOption = {
        value: trimmedValue,
        label: trimmedValue,
        __isNew__: true,
      };

      // Add to selected values
      onChange([...value, newOption]);

      // Notify parent if handler provided
      if (onCreateOption) {
        onCreateOption(trimmedValue);
      }

      // Clear search and close if requested
      setSearchValue("");
      if (closeMenuOnSelect) {
        setOpen(false);
      }
    } finally {
      isCreatingRef.current = false;
      setIsCreating(false);
    }
  };

  // Display text for trigger button
  const triggerText = useMemo(() => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) return value[0].label;
    return `${value.length} items selected`;
  }, [value, placeholder]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchValue) return options;
    const lowerSearch = searchValue.toLowerCase();
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(lowerSearch)
    );
  }, [options, searchValue]);

  return (
    <Popover
      open={open}
      onOpenChange={(newOpen) => {
        if (newOpen) {
          // Reset on open so the next menu render starts from a clean search state.
          setSearchValue("");
        }
        setOpen(newOpen);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "h-10 w-full justify-between",
            "font-inter text-sm font-medium",
            dropdownTriggerClassName,
            className
          )}
        >
          {value.length > 0 ? (
            <span className="text-neutral-800">{triggerText}</span>
          ) : (
            <span className="text-neutral-500">{triggerText}</span>
          )}
          <i
            className={cn(
              "fa-regular fa-chevron-down ml-2 h-4 w-4 shrink-0",
              dropdownChevronClassName
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 border-neutral-100"
        disableAnimation
        style={
          matchTriggerWidth && triggerWidth > 0
            ? {
                width: `${triggerWidth}px`,
                minWidth: `${triggerWidth}px`,
              }
            : undefined
        }
        sideOffset={4}
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search or create..."
            value={searchValue}
            onValueChange={setSearchValue}
            className="h-9 border-neutral-100 font-inter text-sm"
          />
          <CommandList>
            {/* Create option */}
            {showCreateOption && (
              <CommandGroup className="border-b border-neutral-100">
                <CommandItem
                  onSelect={handleCreateOption}
                  className="font-inter font-medium text-sm text-blue-600 cursor-pointer px-3 py-2"
                >
                  <i className="fa-regular fa-plus mr-2 h-4 w-4" />
                  {formatCreateLabel(searchValue)}
                </CommandItem>
              </CommandGroup>
            )}

            {/* Existing options */}
            {filteredOptions.length > 0 ? (
              <CommandGroup>
                {filteredOptions.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={`${option.label}-${option.value}`}
                      onSelect={() => toggleOption(option)}
                      className="font-inter font-normal text-sm text-neutral-600 cursor-pointer data-[selected=true]:text-neutral-900 px-3 py-2"
                    >
                      <div
                        className={cn(
                          "w-4 h-4 rounded border flex items-center justify-center mr-2 flex-shrink-0 transition-colors",
                          isSelected
                            ? "bg-blue-500 border-blue-500 text-white"
                            : "border-neutral-300"
                        )}
                      >
                        {isSelected && <CheckIcon className="h-2.5 w-2.5" />}
                      </div>
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : (
              !showCreateOption && (
                <div className="py-6 text-center font-inter text-sm text-neutral-500">
                  No options found.
                </div>
              )
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
